<template>
  <div class="np-status-mappings-tab">
    <div class="np-status-mappings-tab__toolbar">
      <b-input-group size="sm" class="np-status-mappings-tab__search">
        <b-form-input
          v-model="query"
          placeholder="Пошук: код НП, статус НП або статус CRM…"
        />
        <b-input-group-append>
          <b-button variant="outline-secondary" :disabled="!query" @click="query = ''">
            Очистити
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <b-form-select
        v-model="view"
        size="sm"
        class="np-status-mappings-tab__filter"
        :options="viewOptions"
      />

      <div class="np-status-mappings-tab__actions">
        <b-button size="sm" variant="outline-primary" :disabled="isLoading" @click="refresh">
          <i class="fa" :class="isLoading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
          Оновити
        </b-button>
        <b-button size="sm" variant="primary" @click="openCreateMapping()">
          <i class="fa fa-plus"></i>
          Додати маппінг
        </b-button>
      </div>
    </div>

    <div class="np-status-mappings-tab__meta text-muted">
      <span>Маппінгів: {{ stats.mappings }}</span>
      <span class="mx-2">•</span>
      <span>Статусів НП: {{ stats.npCodes }}</span>
      <span class="mx-2">•</span>
      <span>Не замаплено: {{ stats.unmapped }}</span>
    </div>

    <div v-if="!hasLoaded && isLoading" class="np-status-mappings-tab__loading text-center text-muted">
      <i class="fa fa-spinner fa-spin mr-1"></i>
      Завантаження…
    </div>

    <div v-else class="np-status-mappings-tab__content">
      <div
        v-if="showUnmappedSection"
        class="mapping-section"
        :class="{ 'is-collapsed': isCollapsed('unmapped') }"
      >
        <div class="mapping-section__header" @click="toggleCollapse('unmapped')">
          <div class="mapping-section__title">
            Не використані статуси НП
            <span class="mapping-section__count">{{ filteredUnmapped.length }}</span>
          </div>
          <div class="mapping-section__chevron">
            <i class="fa" :class="isCollapsed('unmapped') ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
          </div>
        </div>
        <div v-show="!isCollapsed('unmapped')" class="mapping-section__body">
          <div v-if="!filteredUnmapped.length" class="text-muted small py-2">
            Немає статусів для відображення.
          </div>
          <div v-else class="chips">
            <button
              v-for="code in filteredUnmapped"
              :key="code.code"
              type="button"
              class="chip chip--unmapped"
              :title="code.name"
              @click="openCreateMapping({ np_status_code: code.code, np_status_name: code.name })"
            >
              <span class="chip__code">{{ code.code }}</span>
              <span class="chip__text">{{ code.name }}</span>
              <span class="chip__hint">
                <i class="fa fa-plus"></i>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div
        v-for="group in filteredCrmGroups"
        :key="group.key"
        class="mapping-section"
        :class="{ 'is-collapsed': isCollapsed(group.key) }"
      >
        <div class="mapping-section__header" @click="toggleCollapse(group.key)">
          <div class="mapping-section__title">
            <span class="crm-dot" :style="{ backgroundColor: group.color }"></span>
            {{ group.title }}
            <span class="mapping-section__count">{{ group.mappings.length }}</span>
          </div>
          <div class="mapping-section__header-actions" @click.stop>
            <b-button
              v-if="group.crmStatusId !== null"
              size="sm"
              variant="link"
              @click="openCreateMapping({ crm_status_id: group.crmStatusId })"
            >
              <i class="fa fa-plus"></i>
            </b-button>
            <b-button size="sm" variant="link" :class="{ 'text-muted': isCollapsed(group.key) }" @click="toggleCollapse(group.key)">
              <i class="fa" :class="isCollapsed(group.key) ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
            </b-button>
          </div>
        </div>
        <div v-show="!isCollapsed(group.key)" class="mapping-section__body">
          <div v-if="!group.mappings.length" class="text-muted small py-2">
            Немає маппінгів.
          </div>
          <div v-else class="chips">
            <div
              v-for="mapping in group.mappings"
              :key="mapping.id"
              class="chip"
              :class="{ 'chip--inactive': !mapping.is_active }"
              :title="mappingTooltip(mapping)"
              @click="openEditMapping(mapping)"
            >
              <span class="chip__code">{{ mapping.np_status_code }}</span>
              <span class="chip__text">{{ mapping.np_status_name }}</span>
              <span v-if="mapping.exceptions && mapping.exceptions.length" class="chip__meta">
                {{ mapping.exceptions.length }}
              </span>
              <button type="button" class="chip__remove" title="Видалити" @click.stop="removeMapping(mapping)">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="hasLoaded && !filteredCrmGroups.length && !showUnmappedSection" class="text-muted small py-3 text-center">
        Немає даних для відображення.
      </div>
    </div>

    <MappingPopup
      v-if="showMappingPopup"
      :editMode="mappingEditMode"
      :mappingData="mappingData"
      :prefill="mappingPrefill"
      @closePopup="closeMappingPopup"
      @save="saveMapping"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'StatusMappingsTab',
  components: {
    MappingPopup: () => import('@/components/popups/plugins/nova-poshta/status-mappings/index.vue'),
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      query: '',
      view: 'mapped',
      isLoading: false,
      hasLoaded: false,
      collapsed: {
        unmapped: true,
      },
      showMappingPopup: false,
      mappingEditMode: false,
      mappingData: null,
      mappingPrefill: null,
    };
  },
  computed: {
    ...mapGetters({
      npCodes: 'npAutomation/npCodes',
      crmStatuses: 'npAutomation/crmStatuses',
      npStatusMappings: 'npAutomation/npStatusMappings',
    }),
    viewOptions() {
      return [
        { value: 'mapped', text: 'Тільки використані' },
        { value: 'unmapped', text: 'Тільки не використані' },
        { value: 'inactive', text: 'Тільки неактивні' },
        { value: 'all', text: 'Всі' },
      ];
    },
    stats() {
      const mappings = Array.isArray(this.npStatusMappings) ? this.npStatusMappings.length : 0;
      const npCodes = this.npCodes ? Object.keys(this.npCodes).length : 0;
      return {
        mappings,
        npCodes,
        unmapped: this.unmappedCodes.length,
      };
    },
    normalizedQuery() {
      return String(this.query || '').trim().toLowerCase();
    },
    crmStatusById() {
      const map = {};
      (this.crmStatuses || []).forEach(status => {
        map[status.id] = status;
      });
      return map;
    },
    mappingsByNpCode() {
      const map = {};
      (this.npStatusMappings || []).forEach(mapping => {
        const code = String(mapping.np_status_code || '');
        if (!code) return;
        map[code] = mapping;
      });
      return map;
    },
    npCodesList() {
      if (!this.npCodes) {
        return [];
      }
      const items = Object.keys(this.npCodes).map(code => ({
        code: String(code),
        name: String(this.npCodes[code] || ''),
      }));
      return items.sort((a, b) => {
        const aNum = Number(a.code);
        const bNum = Number(b.code);
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
          return aNum - bNum;
        }
        return a.code.localeCompare(b.code);
      });
    },
    unmappedCodes() {
      if (!this.npCodesList.length) {
        return [];
      }
      return this.npCodesList.filter(item => !this.mappingsByNpCode[item.code]);
    },
    filteredUnmapped() {
      if (this.view === 'mapped' || this.view === 'inactive') {
        return [];
      }
      if (!this.normalizedQuery) {
        return this.unmappedCodes;
      }
      return this.unmappedCodes.filter(item => {
        return (
          item.code.includes(this.normalizedQuery) ||
          item.name.toLowerCase().includes(this.normalizedQuery)
        );
      });
    },
    showUnmappedSection() {
      if (this.view === 'mapped' || this.view === 'inactive') {
        return false;
      }
      if (this.view === 'unmapped') {
        return true;
      }
      return this.filteredUnmapped.length > 0;
    },
    filteredCrmGroups() {
      if (this.view === 'unmapped') {
        return [];
      }
      const groups = [];
      const crmStatuses = Array.isArray(this.crmStatuses) ? this.crmStatuses : [];

      crmStatuses.forEach(status => {
        const mappings = (this.npStatusMappings || []).filter(mapping => mapping.crm_status_id === status.id);
        groups.push(this.buildGroup({
          key: `crm:${status.id}`,
          title: status.title,
          color: status.color || '#94D974',
          crmStatusId: status.id,
          mappings,
        }));
      });

      const withoutCrm = (this.npStatusMappings || []).filter(mapping => !mapping.crm_status_id);
      if (withoutCrm.length) {
        groups.push(this.buildGroup({
          key: 'crm:none',
          title: 'Без статусу CRM',
          color: '#cfcfcf',
          crmStatusId: null,
          mappings: withoutCrm,
        }));
      }

      const filtered = groups
        .map(group => {
          if (this.view === 'inactive') {
            return {
              ...group,
              mappings: group.mappings.filter(m => !m.is_active),
            };
          }
          return group;
        })
        .filter(group => group.mappings.length > 0 || this.matchesGroupTitle(group));

      if (!this.normalizedQuery) {
        return filtered;
      }

      return filtered
        .map(group => {
          if (this.matchesGroupTitle(group)) {
            return group;
          }
          return {
            ...group,
            mappings: group.mappings.filter(mapping => this.matchesMapping(mapping)),
          };
        })
        .filter(group => group.mappings.length > 0 || this.matchesGroupTitle(group));
    },
  },
  watch: {
    active(value) {
      if (value) {
        this.ensureLoaded();
      }
    },
  },
  created() {
    if (this.active) {
      this.ensureLoaded();
    }
  },
  methods: {
    ...mapActions({
      getNpCodes: 'npAutomation/getNpCodes',
      getCrmStatuses: 'npAutomation/getCrmStatuses',
      getNpStatusMappings: 'npAutomation/getNpStatusMappings',
      createNpStatusMapping: 'npAutomation/createNpStatusMapping',
      updateNpStatusMapping: 'npAutomation/updateNpStatusMapping',
      deleteNpStatusMapping: 'npAutomation/deleteNpStatusMapping',
    }),
    ensureLoaded() {
      if (this.hasLoaded || this.isLoading) {
        return;
      }
      this.refresh();
    },
    refresh() {
      this.isLoading = true;
      return Promise.all([
        this.getNpCodes(),
        this.getCrmStatuses(),
        this.getNpStatusMappings(),
      ]).then(() => {
        this.hasLoaded = true;
      }).finally(() => {
        this.isLoading = false;
      });
    },
    buildGroup(group) {
      return {
        ...group,
        mappings: (group.mappings || []).slice().sort((a, b) => {
          const aCode = String(a.np_status_code || '');
          const bCode = String(b.np_status_code || '');
          const aNum = Number(aCode);
          const bNum = Number(bCode);
          if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
            return aNum - bNum;
          }
          return aCode.localeCompare(bCode);
        }),
      };
    },
    matchesGroupTitle(group) {
      if (!this.normalizedQuery) {
        return false;
      }
      return String(group.title || '').toLowerCase().includes(this.normalizedQuery);
    },
    matchesMapping(mapping) {
      const code = String(mapping.np_status_code || '');
      const npName = String(mapping.np_status_name || '').toLowerCase();
      const crmTitle = String(
        (mapping.crm_status && mapping.crm_status.title) ||
        (this.crmStatusById[mapping.crm_status_id] && this.crmStatusById[mapping.crm_status_id].title) ||
        ''
      ).toLowerCase();
      return (
        code.includes(this.normalizedQuery) ||
        npName.includes(this.normalizedQuery) ||
        crmTitle.includes(this.normalizedQuery)
      );
    },
    mappingTooltip(mapping) {
      const crmTitle = (mapping.crm_status && mapping.crm_status.title) || (this.crmStatusById[mapping.crm_status_id] && this.crmStatusById[mapping.crm_status_id].title) || '—';
      const exceptions = Array.isArray(mapping.exceptions) ? mapping.exceptions : [];
      const exceptionTitles = exceptions
        .map(ex => (ex.crm_status && ex.crm_status.title) || (this.crmStatusById[ex.crm_status_id] && this.crmStatusById[ex.crm_status_id].title) || ex.crm_status_id)
        .filter(Boolean)
        .join(', ');

      const lines = [
        `НП: ${mapping.np_status_code} — ${mapping.np_status_name || '—'}`,
        `CRM: ${crmTitle}`,
        `Активний: ${mapping.is_active ? 'так' : 'ні'}`,
      ];
      if (exceptions.length) {
        lines.push(`Виключення: ${exceptionTitles}`);
      }
      return lines.join('\n');
    },
    openCreateMapping(prefill = {}) {
      this.mappingEditMode = false;
      this.mappingData = null;
      this.mappingPrefill = prefill;
      this.showMappingPopup = true;
    },
    openEditMapping(mapping) {
      this.mappingEditMode = true;
      this.mappingData = mapping;
      this.mappingPrefill = null;
      this.showMappingPopup = true;
    },
    closeMappingPopup() {
      this.showMappingPopup = false;
      this.mappingEditMode = false;
      this.mappingData = null;
      this.mappingPrefill = null;
    },
    saveMapping(payload) {
      const action = this.mappingEditMode
        ? this.updateNpStatusMapping({ id: payload.id, data: payload })
        : this.createNpStatusMapping(payload);

      action.then(res => {
        if (res.status) {
          this.getNpStatusMappings();
          this.$toasted.success(this.mappingEditMode ? 'Маппінг оновлено' : 'Маппінг створено', { duration: 3000 });
          this.closeMappingPopup();
          return;
        }
        const errorMessage = (res.data && res.data.message) || 'Помилка збереження';
        this.$toasted.error(errorMessage, { duration: 3000 });
      });
    },
    removeMapping(mapping) {
      if (!mapping) {
        return;
      }
      if (!confirm('Видалити маппінг?')) {
        return;
      }
      this.deleteNpStatusMapping(mapping.id).then(res => {
        if (res.status) {
          this.getNpStatusMappings();
          this.$toasted.success('Маппінг видалено', { duration: 3000 });
          return;
        }
        this.$toasted.error('Помилка видалення', { duration: 3000 });
      });
    },
    isCollapsed(key) {
      return Boolean(this.collapsed[key]);
    },
    toggleCollapse(key) {
      this.$set(this.collapsed, key, !this.isCollapsed(key));
    },
  },
};
</script>

<style lang="scss" scoped>
.np-status-mappings-tab {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.np-status-mappings-tab__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.np-status-mappings-tab__search {
  min-width: 240px;
  flex: 1 1 320px;
}

.np-status-mappings-tab__filter {
  width: 220px;
  flex: 0 0 220px;
}

.np-status-mappings-tab__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  white-space: nowrap;
  margin-left: auto;
  flex: 1 1 220px;
}

.np-status-mappings-tab__meta {
  font-size: 12px;
}

.np-status-mappings-tab__loading {
  padding: 24px 0;
}

.mapping-section {
  border: 1px solid #ececec;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.mapping-section__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #f8f9fa;
  cursor: pointer;
  user-select: none;
}

.mapping-section__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.mapping-section__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  min-width: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e9ecef;
  font-size: 12px;
  font-weight: 600;
  color: #555;
}

.mapping-section__header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.mapping-section__header-actions ::v-deep .btn.btn-link {
  padding: 0 6px;
}

.mapping-section__chevron {
  color: #6c757d;
}

.mapping-section__body {
  padding: 10px 12px 12px;
}

.crm-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(0, 0, 0, 0.08);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 26px 6px 8px;
  border-radius: 999px;
  border: 1px solid #e2e6ea;
  background: #fff;
  color: #333;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  max-width: 100%;
}

.chip__code {
  font-weight: 700;
  color: #0b7285;
  flex: 0 0 auto;
}

.chip__text {
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chip__meta {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  min-width: 16px;
  padding: 0 5px;
  border-radius: 999px;
  background: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
  font-size: 11px;
  font-weight: 700;
}

.chip__remove {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  padding: 0;
  color: #adb5bd;
  cursor: pointer;
}

.chip__remove:hover {
  color: #dc3545;
}

.chip--inactive {
  opacity: 0.55;
}

.chip--unmapped {
  border-style: dashed;
}

.chip--unmapped .chip__hint {
  color: #6c757d;
  margin-left: 4px;
}

@media (max-width: 720px) {
  .np-status-mappings-tab__filter {
    width: 100%;
  }

  .np-status-mappings-tab__actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .chip__text {
    max-width: 240px;
  }
}
</style>
